import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Search, MessageSquare, Newspaper, CreditCard, Star, Shield, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';

const ROLE_BADGE = {
  admin:  { label:'Admin',     cls:'bg-red-100 text-red-700' },
  paid:   { label:'Paid',      cls:'bg-green-100 text-green-700' },
  gifted: { label:'Gifted 🎁', cls:'bg-purple-100 text-purple-700' },
  free:   { label:'Free',      cls:'bg-gray-100 text-gray-600' },
};

export default function Navbar() {
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const [menu,     setMenu]     = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab,  setAuthTab]  = useState('login');
  const [dropdown, setDropdown] = useState(false);

  const openLogin    = () => { setAuthTab('login');    setAuthOpen(true); setMenu(false); };
  const openRegister = () => { setAuthTab('register'); setAuthOpen(true); setMenu(false); };
  const handleLogout = () => { logout(); setDropdown(false); navigate('/'); };

  const active = path => location.pathname === path ? 'text-yellow-400 font-semibold' : 'hover:text-yellow-400';

  const navLinks = [
    { to:'/search',     icon:<Search size={14}/>,      label:'Search' },
    { to:'/chat',       icon:<MessageSquare size={14}/>,label:'AI Chat' },
    { to:'/blog',       icon:<Newspaper size={14}/>,    label:'News & Articles' },
    { to:'/pricing',    icon:<CreditCard size={14}/>,   label:'Pricing' },
    ...(isLoggedIn()?[{ to:'/collection', icon:<Star size={14}/>, label:'My Collection' }]:[]),
  ];

  return (
    <>
      <nav className="bg-blue-950 text-white px-5 py-3 flex items-center justify-between sticky top-0 z-50 shadow-xl">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center"><BookOpen size={16} className="text-blue-950"/></div>
          <span className="font-bold text-base">LawOC</span>
        </Link>

        <div className="hidden md:flex items-center gap-5 text-sm">
          {navLinks.map(l=>(
            <Link key={l.to} to={l.to} className={"flex items-center gap-1.5 transition "+active(l.to)}>
              {l.icon}{l.label}
            </Link>
          ))}
          {isAdmin()&&(
            <Link to="/admin" className="flex items-center gap-1.5 text-yellow-300 font-semibold hover:text-yellow-400">
              <Shield size={14}/>Admin
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isLoggedIn() ? (
            <>
              <button onClick={openLogin}    className="text-sm text-blue-200 hover:text-white transition px-2">Login</button>
              <button onClick={openRegister} className="bg-yellow-500 text-blue-950 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-yellow-400 transition">Register</button>
            </>
          ) : (
            <div className="relative">
              <button onClick={()=>setDropdown(!dropdown)} className="flex items-center gap-2 hover:text-yellow-300 transition">
                <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center font-bold text-sm">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold leading-none">{user.name}</div>
                  <span className={"text-xs px-1.5 py-0.5 rounded font-medium mt-0.5 inline-block "+(ROLE_BADGE[user.role]?.cls||'')}>
                    {ROLE_BADGE[user.role]?.label||user.role}
                  </span>
                </div>
              </button>
              {dropdown&&(
                <>
                  <div className="fixed inset-0 z-40" onClick={()=>setDropdown(false)}/>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="font-semibold text-gray-800 text-sm truncate">{user.name}</p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    </div>
                    <Link to="/collection" onClick={()=>setDropdown(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"><Star size={14}/>My Collection</Link>
                    {isAdmin()&&<Link to="/admin" onClick={()=>setDropdown(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"><Shield size={14}/>Admin Panel</Link>}
                    <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 w-full text-left"><LogOut size={14}/>Logout</button>
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={()=>setMenu(!menu)} className="md:hidden ml-1">
            {menu?<X size={20}/>:<Menu size={20}/>}
          </button>
        </div>
      </nav>

      {menu&&(
        <div className="md:hidden bg-blue-900 text-white px-5 py-4 space-y-3 shadow-lg">
          {navLinks.map(l=>(
            <Link key={l.to} to={l.to} onClick={()=>setMenu(false)} className="flex items-center gap-2 text-sm py-1 hover:text-yellow-400">{l.icon}{l.label}</Link>
          ))}
          {isAdmin()&&<Link to="/admin" onClick={()=>setMenu(false)} className="flex items-center gap-2 text-sm py-1 text-yellow-300"><Shield size={14}/>Admin Panel</Link>}
          {!isLoggedIn()&&(
            <div className="flex gap-2 pt-2 border-t border-blue-800">
              <button onClick={openLogin}    className="flex-1 bg-blue-800 py-2 rounded-lg text-sm">Login</button>
              <button onClick={openRegister} className="flex-1 bg-yellow-500 text-blue-950 py-2 rounded-lg text-sm font-bold">Register</button>
            </div>
          )}
        </div>
      )}
      <AuthModal open={authOpen} tab={authTab} setTab={setAuthTab} onClose={()=>setAuthOpen(false)}/>
    </>
  );
}
